import React, { useState, useEffect, useRef } from 'react';
import ReactMapGL, { GeolocateControl, Marker } from '@urbica/react-map-gl';
import Draw from '@urbica/react-map-gl-draw';
import { area } from "@turf/turf";
import SaveAsIcon from '@mui/icons-material/SaveAs';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaceIcon from '@mui/icons-material/Place';
import logger from '../../utils/logger';
import authAxios from '../../services/Api';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

const OVERLAY_SOURCE_ID = 'ndvi-overlay-source';
const OVERLAY_LAYER_ID = 'ndvi-overlay-layer';

// Layer switcher options shown only for NDVIAnalyzer (props.ndvi present).
// All three are on-demand for the CURRENTLY drawn polygon — NDVI starts out
// pre-seeded with the saved snapshot (props.ndvi) so opening the modal
// doesn't cost an extra API call, but redrawing invalidates that seed just
// like Water/Soil, so every layer stays consistent with what's on the map.
const LAYER_OPTIONS = [
    { key: 'ndvi', label: 'Vegetation', indexType: 'NDVI' },
    { key: 'ndwi', label: 'Water', indexType: 'NDWI' },
    { key: 'bsi', label: 'Soil', indexType: 'BSI' },
];

const Map = ({ onCloseModal, props, onSaveModal }) => {
    // NDVIAnalyzer fields pass `type` explicitly now (not just `ndvi`) so the
    // layer switcher still shows up for a brand-new field that hasn't been
    // saved/analyzed yet — gating on `ndvi` alone hid it until after save.
    const isNdviField = props?.type === 'NDVIAnalyzer' || !!props?.ndvi;
    // Single-point pin mode: no polygon drawing, just a draggable marker
    // the user can click-to-place, drag, or fill from device GPS.
    const isGeoField = props?.type === 'Geolocation';

    const [viewport, setViewport] = useState({
        latitude: 37.7577,   // fallback
        longitude: -122.4376,
        zoom: 17,
    });

    const [data1, setPolygon] = useState(null);
    const [mapArea, setMapArea] = useState("");
    const [geoMarker, setGeoMarker] = useState(() => {
        const lat = Number(props?.value?.latitude);
        const lon = Number(props?.value?.longitude);
        return Number.isFinite(lat) && Number.isFinite(lon) ? { latitude: lat, longitude: lon } : null;
    });
    const [geoLocating, setGeoLocating] = useState(false);
    const [geoError, setGeoError] = useState('');
    const mapRef = useRef(null);
    // Satellite basemap only matters for NDVI (seeing real crop imagery under
    // the vegetation overlay) — default to it there, leave Map fields on the
    // original light style.
    const [basemap, setBasemap] = useState(isNdviField ? "satellite" : "street");
    const mapStyleUrl = basemap === "satellite"
        ? "mapbox://styles/mapbox/satellite-streets-v11"
        : "mapbox://styles/mapbox/light-v9";

    // Which layer is currently shown. All three keys are on-demand for
    // whatever polygon is currently drawn; each just starts pre-seeded with
    // its saved snapshot (props.ndvi/ndwi/bsi) below so opening the modal
    // doesn't cost an extra API call when nothing has changed.
    const [activeLayer, setActiveLayer] = useState('ndvi');
    const [indexResults, setIndexResults] = useState(() => ({
        ndvi: props?.ndvi || null,
        ndwi: props?.ndwi || null,
        bsi: props?.bsi || null,
    }));
    const [loadingLayer, setLoadingLayer] = useState(null);
    const [layerError, setLayerError] = useState('');

    // The overlay to render right now, whichever layer is active. Kept in a
    // ref (not just state) so the mapbox 'style.load' listener — bound once
    // and long-lived — always reads the CURRENT value instead of a stale
    // closure from whenever it was attached.
    const overlayRef = useRef(null);
    useEffect(() => {
        overlayRef.current = indexResults[activeLayer] || null;
        applyCurrentOverlay();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLayer, indexResults]);

    // Drape whatever overlayRef currently holds onto the map at its exact
    // geographic bounds. Purely additive: only runs when the caller passes
    // `props.ndvi` (NDVIAnalyzer only — Map fields never set this, so Map's
    // own behavior here is unaffected).
    const applyCurrentOverlay = () => {
        if (!mapRef.current) return;
        const map = mapRef.current.getMap();
        if (!map) return;

        const doApply = () => {
            if (map.getLayer(OVERLAY_LAYER_ID)) map.removeLayer(OVERLAY_LAYER_ID);
            if (map.getSource(OVERLAY_SOURCE_ID)) map.removeSource(OVERLAY_SOURCE_ID);
            const overlay = overlayRef.current;
            if (!overlay?.image || !overlay?.imageBounds) return;
            map.addSource(OVERLAY_SOURCE_ID, {
                type: 'image',
                url: overlay.image,
                coordinates: overlay.imageBounds,
            });
            map.addLayer({
                id: OVERLAY_LAYER_ID,
                type: 'raster',
                source: OVERLAY_SOURCE_ID,
                paint: { 'raster-opacity': 0.75 },
            });
        };

        if (map.isStyleLoaded()) {
            doApply();
        } else {
            map.once('load', doApply);
        }
    };

    // Switching the basemap style (satellite <-> street) makes mapbox-gl
    // reload its whole style, which wipes custom sources/layers — so the
    // overlay needs to be re-applied every time the style finishes reloading,
    // not just on the very first load.
    const handleMapLoad = () => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        map.on('style.load', applyCurrentOverlay);
        applyCurrentOverlay();
    };

    // Fetches an index on demand for whichever polygon is currently drawn
    // (falls back to the originally saved value if nothing new was drawn
    // this session). Cached in indexResults so re-selecting the same layer
    // without redrawing doesn't re-fetch; onDrawCreate clears the cache
    // whenever the boundary actually changes, forcing a fresh fetch.
    const handleLayerClick = async (option) => {
        setLayerError('');
        if (indexResults[option.key]) {
            setActiveLayer(option.key);
            return;
        }
        const coordinates = data1?.features?.[0]?.geometry?.coordinates || props?.value;
        if (!coordinates || coordinates.length === 0) {
            setLayerError('Draw a boundary first.');
            return;
        }
        setLoadingLayer(option.key);
        try {
            const response = await authAxios.post('/api/v1/formSubmission/ndviIndex', {
                coordinates,
                indexType: option.indexType,
            });
            setIndexResults((prev) => ({ ...prev, [option.key]: response.data.data }));
            setActiveLayer(option.key);
        } catch (err) {
            logger.warn('On-demand index fetch failed:', err);
            setLayerError(err?.response?.data?.message || `Could not compute ${option.label}.`);
        } finally {
            setLoadingLayer(null);
        }
    };

    // Center map: prefer the pinned point (Geolocation) or polygon coords
    // (Map/NDVI), else fall back to the device's current position.
    useEffect(() => {
        if (isGeoField && geoMarker) {
            setViewport((prev) => ({
                ...prev,
                latitude: geoMarker.latitude,
                longitude: geoMarker.longitude,
                zoom: 17,
            }));
        } else if (isGeoField) {
            // No saved pin yet — just center on the device's current
            // position for convenience; the pin itself is only placed
            // by an explicit click, drag, or "Use my location" tap.
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        setViewport((prev) => ({
                            ...prev,
                            latitude: pos.coords.latitude,
                            longitude: pos.coords.longitude,
                            zoom: 17,
                        }));
                    },
                    (err) => logger.warn("Geolocation error:", err)
                );
            }
        } else if (props.value && props.value[0] && props.value[0][0]) {
            const [lng, lat] = props.value[0][0];
            setViewport((prev) => ({
                ...prev,
                latitude: lat,
                longitude: lng,
                zoom: 17,
            }));
        } else if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setViewport((prev) => ({
                        ...prev,
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                        zoom: 17,
                    }));
                },
                (err) => logger.warn("Geolocation error:", err)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    // Load polygon from props
    useEffect(() => {
        if (props.value && props.value.length > 0) {
            const data = {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        properties: {},
                        geometry: {
                            type: "Polygon",
                            coordinates: props.value,
                        },
                    },
                ],
            };
            setPolygon(data);
        }
    }, [props.value]);

    // Calculate area when polygon changes
    useEffect(() => {
        if (data1 && data1.features.length > 0) {
            const poly = data1.features[0];
            if (poly.geometry.type === "Polygon") {
                const areaInSquareMeters = area(poly);
                const acres = (areaInSquareMeters * 0.00024710538146717).toFixed(3);
                setMapArea(acres);
            }
        } else {
            setMapArea("");
        }
    }, [data1]);

    const handleViewportChange = (newViewport) => {
        setViewport(newViewport);
    };

const onDrawCreate = (event) => {
  // Boundary changed (drawn or deleted) — every previously computed layer
  // (including the pre-seeded NDVI snapshot) is now stale for this shape,
  // so clear the cache and let the next layer selection fetch fresh.
  setIndexResults({});
  setActiveLayer('ndvi');
  if (event.features.length > 0) {
    const newFeature = event.features[0];
    setPolygon({
      type: "FeatureCollection",
      features: [newFeature],
    });
  } else {
    setPolygon(null);
    setMapArea("");
  }
};


    const closeCustomModal = () => {
        setPolygon(null);
        onCloseModal(false);
    };

    // Drop/move the pin to wherever the user clicks on the map.
    const handleMapClick = (event) => {
        if (!isGeoField || !event?.lngLat) return;
        setGeoError('');
        setGeoMarker({ latitude: event.lngLat.lat, longitude: event.lngLat.lng });
    };

    // Fill the pin from the device's own GPS, same as the request's
    // "Save button to capture the user's geolocation".
    const handleUseMyLocation = () => {
        if (!navigator.geolocation) {
            setGeoError('Geolocation is not supported on this device.');
            return;
        }
        setGeoError('');
        setGeoLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const next = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
                setGeoMarker(next);
                setViewport((prev) => ({ ...prev, ...next, zoom: 17 }));
                setGeoLocating(false);
            },
            (err) => {
                logger.warn('Geolocation error:', err);
                setGeoError('Could not get your location. Please allow location access or pin it on the map.');
                setGeoLocating(false);
            }
        );
    };

    const saveModal = () => {
        let localValue = {};
        localValue.name = props.name;

        if (isGeoField) {
            localValue.value = geoMarker
                ? { latitude: geoMarker.latitude, longitude: geoMarker.longitude }
                : { latitude: "", longitude: "" };
            localStorage.setItem("mapValues", JSON.stringify(localValue));
            onSaveModal(localValue.value);
            onCloseModal(false);
            return;
        }

        if (data1 && data1.features[0]) {
            localValue.value = data1.features[0].geometry.coordinates;
            localValue.area = mapArea;
        } else {
            localValue.value = "";
            localValue.area = "";
        }

        // Persist any on-demand Water/Soil results fetched during this
        // session, so reopening the submission doesn't need to re-fetch them.
        // NDVI itself is excluded — it's already recomputed fresh at save
        // time server-side (analyzeNDVIInForm), so carrying a client-side
        // copy here would just be a redundant, possibly-stale duplicate.
        localValue.ndwi = indexResults.ndwi || null;
        localValue.bsi = indexResults.bsi || null;

        localStorage.setItem("mapValues", JSON.stringify(localValue));
        onSaveModal(data1 ? data1.features : []);
        onCloseModal(false);
    };

    const activeResult = indexResults[activeLayer];

    return (
        <div id="myModal" className="farm_modal-map">
            <div className="farm_modal-content">
                <div className="farm_modal-header">
                    <span className="farm_save">
                        {isGeoField
                            ? (geoMarker
                                ? `Pinned : ${geoMarker.latitude.toFixed(6)}, ${geoMarker.longitude.toFixed(6)}`
                                : "Tap the map to drop a pin")
                            : `Mapped Area : ${mapArea ? `${mapArea} acre` : ""}`}
                    </span>

                    <span className="farm_close" title="Save">
                        <SaveAsIcon
                            style={{ textTransform: "none", cursor: "pointer", color: "gray" }}
                            onClick={saveModal}
                        />
                    </span>
                    <span className="farm_close" title="Close">
                        <CloseIcon
                            style={{ textTransform: "none", cursor: "pointer", color: "gray" }}
                            onClick={closeCustomModal}
                        />
                    </span>
                </div>
                {isNdviField && (
                    <div className="farm_ndvi_overlay_caption">
                        <select
                            className="farm_layer_select"
                            value={activeLayer}
                            disabled={!!loadingLayer}
                            onChange={(e) => {
                                const option = LAYER_OPTIONS.find((o) => o.key === e.target.value);
                                if (option) handleLayerClick(option);
                            }}
                        >
                            {LAYER_OPTIONS.map((option) => (
                                <option key={option.key} value={option.key}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <span className="farm_ndvi_caption_text">
                            {loadingLayer
                                ? `Loading ${LAYER_OPTIONS.find((o) => o.key === loadingLayer)?.label}...`
                                : activeResult?.error
                                    ? activeResult.error
                                    : activeResult
                                        ? `${LAYER_OPTIONS.find((o) => o.key === activeLayer)?.label}${activeResult.provider === "mock" ? " (mock)" : ""}: ${activeResult.value} (${activeResult.classification})${activeResult.imageUploadFailed ? " — image unavailable (upload failed)" : ""}`
                                        : "Select a layer to view it."}
                        </span>
                        <button
                            type="button"
                            className="farm_basemap_toggle"
                            onClick={() => setBasemap((prev) => (prev === "satellite" ? "street" : "satellite"))}
                        >
                            {basemap === "satellite" ? "Map view" : "Satellite view"}
                        </button>
                    </div>
                )}
                {isGeoField && (
                    <div className="farm_ndvi_overlay_caption farm_geo_caption">
                        <button
                            type="button"
                            className="farm_geo_locate_btn"
                            disabled={geoLocating}
                            onClick={handleUseMyLocation}
                        >
                            <MyLocationIcon fontSize="small" />
                            {geoLocating ? "Locating…" : "Use my location"}
                        </button>
                        <span className="farm_ndvi_caption_text">
                            Click or drag the pin, or use your current location.
                        </span>
                    </div>
                )}
                {geoError && <div className="farm_layer_error">{geoError}</div>}
                {layerError && <div className="farm_layer_error">{layerError}</div>}
                <ReactMapGL
                    ref={mapRef}
                    zoom={viewport.zoom}
                    attributionControl={false}
                    style={{ width: "100%", height: "400px" }}
                    mapStyle={mapStyleUrl}
                    accessToken="pk.eyJ1IjoiYXJqdW5yYW1kYXMiLCJhIjoiY2w0d2hhYWU2MDZobzNqbjhra2dicjdyaSJ9.dweJr9SAdrn0kuxfUokxvg"
                    onViewportChange={handleViewportChange}
                    onLoad={handleMapLoad}
                    onClick={handleMapClick}
                    {...viewport}
                >
                    <GeolocateControl
                        position="top-right"
                        fitBoundsOptions={{ maxZoom: 20 }}
                        trackUserLocation
                        showUserLocation
                    />

                    {isGeoField ? (
                        geoMarker && (
                            <Marker
                                latitude={geoMarker.latitude}
                                longitude={geoMarker.longitude}
                                anchor="bottom"
                                draggable
                                onDragEnd={(lngLat) => {
                                    setGeoError('');
                                    setGeoMarker({ latitude: lngLat.lat, longitude: lngLat.lng });
                                }}
                            >
                                <PlaceIcon
                                    style={{ color: "#d32f2f", fontSize: 36, cursor: "grab" }}
                                />
                            </Marker>
                        )
                    ) : (
                        <Draw
                            controls={{ polygon: true, trash: true }}
                            data={data1 || { type: "FeatureCollection", features: [] }}
                            defaultMode="simple_select"
                            displayControlsDefault={false}
                            onChange={onDrawCreate}
                        />
                    )}
                </ReactMapGL>
            </div>
        </div>
    );
};

export default Map;
